/**
 * Anagrafica delle province italiane con la regione di appartenenza.
 *
 * La provincia sui dati non ha un formato unico: i movimenti la salvano con il
 * nome ISTAT preso dalla tabella comuni ("Torino", "Reggio nell'Emilia"),
 * la rubrica con la sigla automobilistica ("TO"). La ricerca accetta quindi
 * entrambe le forme, piu gli alias di uso comune.
 */

export interface ProvinciaInfo {
  sigla: string;
  /** Nome usato per la visualizzazione */
  nome: string;
  regione: string;
  /** Varianti accettate in ricerca (nome ISTAT, forme abbreviate) */
  alias?: string[];
}

export const PROVINCE: ProvinciaInfo[] = [
  // Piemonte
  { sigla: 'TO', nome: 'Torino', regione: 'Piemonte' },
  { sigla: 'VC', nome: 'Vercelli', regione: 'Piemonte' },
  { sigla: 'NO', nome: 'Novara', regione: 'Piemonte' },
  { sigla: 'CN', nome: 'Cuneo', regione: 'Piemonte' },
  { sigla: 'AT', nome: 'Asti', regione: 'Piemonte' },
  { sigla: 'AL', nome: 'Alessandria', regione: 'Piemonte' },
  { sigla: 'BI', nome: 'Biella', regione: 'Piemonte' },
  { sigla: 'VB', nome: 'Verbano-Cusio-Ossola', regione: 'Piemonte', alias: ['Verbania'] },

  // Valle d'Aosta
  {
    sigla: 'AO',
    nome: 'Aosta',
    regione: "Valle d'Aosta",
    alias: ["Valle d'Aosta/Vallée d'Aoste", "Valle d'Aosta"],
  },

  // Lombardia
  { sigla: 'VA', nome: 'Varese', regione: 'Lombardia' },
  { sigla: 'CO', nome: 'Como', regione: 'Lombardia' },
  { sigla: 'SO', nome: 'Sondrio', regione: 'Lombardia' },
  { sigla: 'MI', nome: 'Milano', regione: 'Lombardia' },
  { sigla: 'BG', nome: 'Bergamo', regione: 'Lombardia' },
  { sigla: 'BS', nome: 'Brescia', regione: 'Lombardia' },
  { sigla: 'PV', nome: 'Pavia', regione: 'Lombardia' },
  { sigla: 'CR', nome: 'Cremona', regione: 'Lombardia' },
  { sigla: 'MN', nome: 'Mantova', regione: 'Lombardia' },
  { sigla: 'LC', nome: 'Lecco', regione: 'Lombardia' },
  { sigla: 'LO', nome: 'Lodi', regione: 'Lombardia' },
  { sigla: 'MB', nome: 'Monza e della Brianza', regione: 'Lombardia', alias: ['Monza e Brianza', 'Monza'] },

  // Trentino-Alto Adige
  { sigla: 'BZ', nome: 'Bolzano', regione: 'Trentino-Alto Adige', alias: ['Bolzano/Bozen', 'Bozen'] },
  { sigla: 'TN', nome: 'Trento', regione: 'Trentino-Alto Adige' },

  // Veneto
  { sigla: 'VR', nome: 'Verona', regione: 'Veneto' },
  { sigla: 'VI', nome: 'Vicenza', regione: 'Veneto' },
  { sigla: 'BL', nome: 'Belluno', regione: 'Veneto' },
  { sigla: 'TV', nome: 'Treviso', regione: 'Veneto' },
  { sigla: 'VE', nome: 'Venezia', regione: 'Veneto' },
  { sigla: 'PD', nome: 'Padova', regione: 'Veneto' },
  { sigla: 'RO', nome: 'Rovigo', regione: 'Veneto' },

  // Friuli-Venezia Giulia
  { sigla: 'UD', nome: 'Udine', regione: 'Friuli-Venezia Giulia' },
  { sigla: 'GO', nome: 'Gorizia', regione: 'Friuli-Venezia Giulia' },
  { sigla: 'TS', nome: 'Trieste', regione: 'Friuli-Venezia Giulia' },
  { sigla: 'PN', nome: 'Pordenone', regione: 'Friuli-Venezia Giulia' },

  // Liguria
  { sigla: 'IM', nome: 'Imperia', regione: 'Liguria' },
  { sigla: 'SV', nome: 'Savona', regione: 'Liguria' },
  { sigla: 'GE', nome: 'Genova', regione: 'Liguria' },
  { sigla: 'SP', nome: 'La Spezia', regione: 'Liguria' },

  // Emilia-Romagna
  { sigla: 'PC', nome: 'Piacenza', regione: 'Emilia-Romagna' },
  { sigla: 'PR', nome: 'Parma', regione: 'Emilia-Romagna' },
  { sigla: 'RE', nome: 'Reggio Emilia', regione: 'Emilia-Romagna', alias: ["Reggio nell'Emilia"] },
  { sigla: 'MO', nome: 'Modena', regione: 'Emilia-Romagna' },
  { sigla: 'BO', nome: 'Bologna', regione: 'Emilia-Romagna' },
  { sigla: 'FE', nome: 'Ferrara', regione: 'Emilia-Romagna' },
  { sigla: 'RA', nome: 'Ravenna', regione: 'Emilia-Romagna' },
  { sigla: 'FC', nome: 'Forlì-Cesena', regione: 'Emilia-Romagna' },
  { sigla: 'RN', nome: 'Rimini', regione: 'Emilia-Romagna' },

  // Toscana
  { sigla: 'MS', nome: 'Massa-Carrara', regione: 'Toscana', alias: ['Massa Carrara'] },
  { sigla: 'LU', nome: 'Lucca', regione: 'Toscana' },
  { sigla: 'PT', nome: 'Pistoia', regione: 'Toscana' },
  { sigla: 'FI', nome: 'Firenze', regione: 'Toscana' },
  { sigla: 'LI', nome: 'Livorno', regione: 'Toscana' },
  { sigla: 'PI', nome: 'Pisa', regione: 'Toscana' },
  { sigla: 'AR', nome: 'Arezzo', regione: 'Toscana' },
  { sigla: 'SI', nome: 'Siena', regione: 'Toscana' },
  { sigla: 'GR', nome: 'Grosseto', regione: 'Toscana' },
  { sigla: 'PO', nome: 'Prato', regione: 'Toscana' },

  // Umbria
  { sigla: 'PG', nome: 'Perugia', regione: 'Umbria' },
  { sigla: 'TR', nome: 'Terni', regione: 'Umbria' },

  // Marche
  { sigla: 'PU', nome: 'Pesaro e Urbino', regione: 'Marche', alias: ['Pesaro'] },
  { sigla: 'AN', nome: 'Ancona', regione: 'Marche' },
  { sigla: 'MC', nome: 'Macerata', regione: 'Marche' },
  { sigla: 'AP', nome: 'Ascoli Piceno', regione: 'Marche' },
  { sigla: 'FM', nome: 'Fermo', regione: 'Marche' },

  // Lazio
  { sigla: 'VT', nome: 'Viterbo', regione: 'Lazio' },
  { sigla: 'RI', nome: 'Rieti', regione: 'Lazio' },
  { sigla: 'RM', nome: 'Roma', regione: 'Lazio' },
  { sigla: 'LT', nome: 'Latina', regione: 'Lazio' },
  { sigla: 'FR', nome: 'Frosinone', regione: 'Lazio' },

  // Abruzzo
  { sigla: 'AQ', nome: "L'Aquila", regione: 'Abruzzo', alias: ['Aquila'] },
  { sigla: 'TE', nome: 'Teramo', regione: 'Abruzzo' },
  { sigla: 'PE', nome: 'Pescara', regione: 'Abruzzo' },
  { sigla: 'CH', nome: 'Chieti', regione: 'Abruzzo' },

  // Molise
  { sigla: 'CB', nome: 'Campobasso', regione: 'Molise' },
  { sigla: 'IS', nome: 'Isernia', regione: 'Molise' },

  // Campania
  { sigla: 'CE', nome: 'Caserta', regione: 'Campania' },
  { sigla: 'BN', nome: 'Benevento', regione: 'Campania' },
  { sigla: 'NA', nome: 'Napoli', regione: 'Campania' },
  { sigla: 'AV', nome: 'Avellino', regione: 'Campania' },
  { sigla: 'SA', nome: 'Salerno', regione: 'Campania' },

  // Puglia
  { sigla: 'FG', nome: 'Foggia', regione: 'Puglia' },
  { sigla: 'BA', nome: 'Bari', regione: 'Puglia' },
  { sigla: 'TA', nome: 'Taranto', regione: 'Puglia' },
  { sigla: 'BR', nome: 'Brindisi', regione: 'Puglia' },
  { sigla: 'LE', nome: 'Lecce', regione: 'Puglia' },
  { sigla: 'BT', nome: 'Barletta-Andria-Trani', regione: 'Puglia' },

  // Basilicata
  { sigla: 'PZ', nome: 'Potenza', regione: 'Basilicata' },
  { sigla: 'MT', nome: 'Matera', regione: 'Basilicata' },

  // Calabria
  { sigla: 'CS', nome: 'Cosenza', regione: 'Calabria' },
  { sigla: 'CZ', nome: 'Catanzaro', regione: 'Calabria' },
  { sigla: 'RC', nome: 'Reggio Calabria', regione: 'Calabria', alias: ['Reggio di Calabria'] },
  { sigla: 'KR', nome: 'Crotone', regione: 'Calabria' },
  { sigla: 'VV', nome: 'Vibo Valentia', regione: 'Calabria' },

  // Sicilia
  { sigla: 'TP', nome: 'Trapani', regione: 'Sicilia' },
  { sigla: 'PA', nome: 'Palermo', regione: 'Sicilia' },
  { sigla: 'ME', nome: 'Messina', regione: 'Sicilia' },
  { sigla: 'AG', nome: 'Agrigento', regione: 'Sicilia' },
  { sigla: 'CL', nome: 'Caltanissetta', regione: 'Sicilia' },
  { sigla: 'EN', nome: 'Enna', regione: 'Sicilia' },
  { sigla: 'CT', nome: 'Catania', regione: 'Sicilia' },
  { sigla: 'RG', nome: 'Ragusa', regione: 'Sicilia' },
  { sigla: 'SR', nome: 'Siracusa', regione: 'Sicilia' },

  // Sardegna
  { sigla: 'SS', nome: 'Sassari', regione: 'Sardegna' },
  { sigla: 'NU', nome: 'Nuoro', regione: 'Sardegna' },
  { sigla: 'CA', nome: 'Cagliari', regione: 'Sardegna' },
  { sigla: 'OR', nome: 'Oristano', regione: 'Sardegna' },
  { sigla: 'SU', nome: 'Sud Sardegna', regione: 'Sardegna' },
  // Province sarde soppresse nel 2016: restano per i dati storici
  { sigla: 'OT', nome: 'Olbia-Tempio', regione: 'Sardegna' },
  { sigla: 'OG', nome: 'Ogliastra', regione: 'Sardegna' },
  { sigla: 'VS', nome: 'Medio Campidano', regione: 'Sardegna' },
  { sigla: 'CI', nome: 'Carbonia-Iglesias', regione: 'Sardegna' },
];

/** Etichetta usata quando la provincia non e riconosciuta */
export const REGIONE_SCONOSCIUTA = 'Non classificata';

/**
 * Chiave di confronto: maiuscole, senza accenti e senza segni di interpunzione,
 * cosi "Forlì-Cesena", "forli cesena" e "FORLI'-CESENA" finiscono sulla stessa voce.
 */
const chiave = (valore: string): string =>
  valore
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

const INDICE: Record<string, ProvinciaInfo> = {};
for (const provincia of PROVINCE) {
  INDICE[chiave(provincia.sigla)] = provincia;
  INDICE[chiave(provincia.nome)] = provincia;
  for (const alias of provincia.alias || []) {
    INDICE[chiave(alias)] = provincia;
  }
}

/** Cerca la provincia per sigla, nome ISTAT o alias */
export const getProvinciaInfo = (provincia?: string | null): ProvinciaInfo | null => {
  if (!provincia) return null;
  return INDICE[chiave(provincia)] || null;
};

/** Regione di appartenenza, o "Non classificata" se la provincia non e riconosciuta */
export const getRegione = (provincia?: string | null): string =>
  getProvinciaInfo(provincia)?.regione || REGIONE_SCONOSCIUTA;

/** Elenco ordinato delle regioni italiane */
export const REGIONI: string[] = [...new Set(PROVINCE.map((p) => p.regione))].sort((a, b) =>
  a.localeCompare(b, 'it')
);
